import React, { useEffect, useState } from "react";
import { MdComputer, MdCheckCircle, MdError, MdDevices } from "react-icons/md";
import Card from "../../../components/card";
import EquipamentTable from "./components/EquipamentTable";
import SkeletonTable from "../../../components/skeleton/table";
import api from "../../../services/axios";
import { Equipament } from "../../../interfaces/Equipament";

const KPI_ACCENTS = {
  blue: { tile: "bg-gradient-to-br from-dv-blue-500 to-dv-blue-600", glow: "rgba(20,102,227,0.20)" },
  cyan: { tile: "bg-gradient-to-br from-dv-cyan-400 to-dv-cyan-600", glow: "rgba(28,160,241,0.22)" },
  emerald: { tile: "bg-gradient-to-br from-emerald-500 to-emerald-600", glow: "rgba(16,185,129,0.20)" },
  red: { tile: "bg-gradient-to-br from-red-500 to-red-600", glow: "rgba(239,68,68,0.20)" },
} as const;

const Kpi: React.FC<{
  icon: JSX.Element;
  label: string;
  value: string;
  accent: keyof typeof KPI_ACCENTS;
}> = ({ icon, label, value, accent }) => {
  const a = KPI_ACCENTS[accent];
  return (
    <Card extra="group relative overflow-hidden p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-dv-lg">
      <div
        className="pointer-events-none absolute -right-10 -top-12 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 group-hover:opacity-90"
        style={{ background: a.glow }}
        aria-hidden="true"
      />
      <div
        className={`relative mb-3 flex h-11 w-11 items-center justify-center rounded-2xl ${a.tile} text-white`}
        style={{ boxShadow: `0 12px 30px -10px ${a.glow}` }}
      >
        {icon}
      </div>
      <p className="relative text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <p className="relative font-display text-3xl font-bold tabular-nums tracking-tight text-navy-700 dark:text-white">
        {value}
      </p>
    </Card>
  );
};

export default function EquipamentManagement() {
  const [computers, setComputers] = useState<Equipament[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchComputers = async () => {
    setLoading(true);
    try {
      // Tente buscar da API
      const response = await api.get("/Equipament");
      setComputers(response.data);
    } catch (error) {
      console.error("Error fetching computers, using mock data:", error);
      // Use dados mockados se a API não estiver disponível
      setComputers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComputers();
  }, []);

  // Contadores por status
  const totalComputers = computers.length;
  const inUseCount = computers.filter((c) => c.status === "em_uso").length;
  const availableCount = computers.filter((c) => c.status === "disponivel").length;
  const damagedCount = computers.filter((c) => c.status === "danificado").length;

  return (
    <div className="mt-5 flex flex-col gap-5 animate-fade-up">
      {/* Cabeçalho da página */}
      <div className="flex items-center gap-4">
        <div
          className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
          aria-hidden="true"
        >
          <MdDevices className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
            Gestão de equipamentos
          </span>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
            Equipamentos
          </h1>
        </div>
      </div>

      {/* Widgets de resumo */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          icon={<MdDevices className="h-6 w-6" aria-hidden="true" />}
          label="Total de Equipamentos"
          value={totalComputers.toString()}
          accent="blue"
        />
        <Kpi
          icon={<MdComputer className="h-6 w-6" aria-hidden="true" />}
          label="Em Uso"
          value={inUseCount.toString()}
          accent="cyan"
        />
        <Kpi
          icon={<MdCheckCircle className="h-6 w-6" aria-hidden="true" />}
          label="Disponíveis"
          value={availableCount.toString()}
          accent="emerald"
        />
        <Kpi
          icon={<MdError className="h-6 w-6" aria-hidden="true" />}
          label="Danificados"
          value={damagedCount.toString()}
          accent="red"
        />
      </div>

      {/* Tabela de equipamentos */}
      <div className="mt-5 grid h-full grid-cols-1 gap-5">
        {loading ? (
          <SkeletonTable active={loading} />
        ) : (
          <EquipamentTable data={computers} onRefresh={fetchComputers} />
        )}
      </div>
    </div>
  );
}